import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Product {
  id: number;
  name: string;
  imageUrl: string;
}

interface SelectionBoxProps {
  products: Product[];
  type: 'protein' | 'compound';
  onSelect: (product: Product | null) => void;
  apiBaseUrl: string;
  apiHeaders: Record<string, string>;
}

const SelectionBox: React.FC<SelectionBoxProps> = ({ products, type, onSelect, apiBaseUrl, apiHeaders }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchImage = async () => {
      if (selectedProduct) {
        setIsLoading(true);
        setError(null);
        try {
          let endpoint;
          if (type === 'protein') {
            endpoint = `/visualization/protein/${selectedProduct.name}`;
          } else if (type === 'compound') {
            endpoint = `/visualization/molecule/${selectedProduct.name}`;
          }
          
          const response = await axios.get(`${apiBaseUrl}${endpoint}`, {
            headers: apiHeaders
          });
          
          if (response.data) {
            // we replace minio:9000 with localhost:9000
            const visualizationUrl = response.data.visualizationUrl || response.data.image_url;
            setImageUrl(visualizationUrl);
          } else {
            setError("No visualization data received");
          }
        } catch (error: any) {
          console.error(`Error fetching ${type} image:`, error);
          setError(error.response?.data?.message || `Failed to load ${type} visualization`);
          setImageUrl(null);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchImage();
  }, [selectedProduct, type, apiBaseUrl, apiHeaders]);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    onSelect(product);
    // Reset image state when selecting a new product
    if (type === 'protein') {
      setImageUrl(null);
      setIsLoading(true);
      setError(null);
    }
  };

  return (
    <div className="p-1 bg-base">
      {/* Preview Box */}
      <div className="mb-5 p-2 flex justify-center items-center bg-white">
        {selectedProduct ? (
          ((isLoading || !imageUrl)) ? (
            <div className="w-60 h-60 border flex flex-col items-center justify-center">
              {isLoading ? (
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              ) : error ? (
                <div className="text-red-500 text-center p-2 text-sm">
                  <p>{error}</p>
                  <button 
                    className="mt-2 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                    onClick={() => {
                      setError(null);
                      setIsLoading(true);
                      // Re-trigger image fetch by updating the state
                      setSelectedProduct({...selectedProduct});
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="text-gray-400">No image available</div>
              )}
            </div>
          ) : (
            <img
              src={imageUrl || selectedProduct.imageUrl}
              alt={`${selectedProduct.name} structure`}
              className="w-60 h-60 border object-contain"
            />
          )
        ) : (
          <div className="w-60 h-60 border flex items-center justify-center text-gray-400">
            Select a {type === 'protein' ? 'target' : 'compound'}
          </div>
        )}
      </div>

      {/* Product List */}
      <div className="bg-gray-base border p-2 rounded shadow-md">
        <div className="grid grid-cols-1 gap-1 max-h-60 bg-gray-base overflow-y-auto rounded">
          {products.length > 0 ? (
            products.map((product) => (
              <div
                key={product.id}
                className={`p-1.5 border bg-base rounded cursor-pointer hover:bg-upload-button-hover active:bg-upload-button-pressed flex items-center font-space-grotesk ${
                  selectedProduct?.id === product.id ? 'bg-upload-button-pressed' : ''
                }`}
                onClick={() => handleProductSelect(product)}
              >
                <span className="truncate w-full" title={product.name}>
                  {product.name}
                </span>
              </div>
            ))
          ) : (
            <div className="p-2 text-center text-gray-500">
              No {type === 'protein' ? 'targets' : 'compounds'} available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectionBox;